import { Stack, Text, textVariants, vars } from '@etg/wings';
import { Circle } from '@phosphor-icons/react';
import { css } from '@eti/styles';
import { useDateFormat, useTranslation } from '@eti/providers';
import { date } from '@eti/utils';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import Time from '../../../../../../common/time/Time';
import { screenReadersOnlyStyles } from '../../../../../../utils/styleUtils';

const originalTimeStyles = css`
  color: #737373;
  text-decoration: line-through;
`;

const timeStyles = (isTimeChanged: boolean) => css`
  background-color: ${isTimeChanged ? vars.colors.signal.caution.background : 'none'};
  width: fit-content;
`;

const getScreenReaderTimeTextKey = (isTimeChanged: boolean, isStartingPoint: boolean = false) => {
  if (isStartingPoint) {
    return isTimeChanged
      ? 'ScreenReader.TripDetails.Details.NewDepartureTime'
      : 'ScreenReader.TripDetails.Details.DepartureTime';
  }

  return isTimeChanged
    ? 'ScreenReader.TripDetails.Details.NewArrivalTime'
    : 'ScreenReader.TripDetails.Details.ArrivalTime';
};

interface ItineraryLocationTimeProps {
  airportCode: string;
  airportName: string;
  cityName: string;
  isLastStop?: boolean;
  isStartingPoint?: boolean;
  originalTime?: string | null;
  time: string;
}

const ItineraryLocationTime = ({
  airportCode,
  airportName,
  cityName,
  isLastStop,
  isStartingPoint,
  originalTime,
  time,
}: ItineraryLocationTimeProps) => {
  const { t } = useTranslation();
  const { time: timeFormat } = useDateFormat();

  const borderColorBefore = isStartingPoint
    ? vars.colors.inputs.border
    : vars.colors.decoration.primary;
  const borderColorAfter = isStartingPoint
    ? vars.colors.decoration.primary
    : vars.colors.inputs.border;
  const borderStyleBefore = isStartingPoint ? 'dashed' : 'solid';
  const borderStyleAfter = isStartingPoint ? 'solid' : 'dashed';
  const formattedOriginalTime = originalTime ? date.format(originalTime, timeFormat) : null;
  const formattedTime = date.format(time, timeFormat);
  const isTimeChanged = Boolean(formattedOriginalTime) && formattedTime !== formattedOriginalTime;

  const dataTestIdPrefix = `itinerary-location-time-${isStartingPoint ? 'departure' : 'arrival'}`;

  return (
    <TimelineRow data-testid={dataTestIdPrefix}>
      <TimelineWrapper
        borderColorAfter={borderColorAfter}
        borderColorBefore={borderColorBefore}
        borderStyleAfter={isLastStop ? '' : borderStyleAfter}
        borderStyleBefore={borderStyleBefore}
        childSize={10}
      >
        <Circle
          aria-hidden
          color={vars.colors.decoration.primary}
          data-testid={`${dataTestIdPrefix}-circle`}
          size={10}
          weight="fill"
          width={21}
        />
      </TimelineWrapper>
      <Stack spacing={0}>
        <Stack spacing={4}>
          <Text
            as={Time}
            className={timeStyles(isTimeChanged)}
            data-testid={`${dataTestIdPrefix}-time`}
            date={time}
            format="time"
            screenReaderTextKey={getScreenReaderTimeTextKey(isTimeChanged, isStartingPoint)}
            variant={textVariants._16_BOLD}
          >
            {formattedTime}
          </Text>
          {isTimeChanged && originalTime && (
            <>
              <Text
                as={Time}
                className={originalTimeStyles}
                data-testid={`${dataTestIdPrefix}-original-time`}
                date={originalTime}
                format="time"
                screenReaderTextKey={
                  isStartingPoint
                    ? 'ScreenReader.TripDetails.Details.OriginalDepartureTime'
                    : 'ScreenReader.TripDetails.Details.OriginalArrivalTime'
                }
                variant={textVariants._16_BOLD}
              >
                {formattedOriginalTime}
              </Text>
            </>
          )}
          <Text className={screenReadersOnlyStyles}>
            {t(
              isStartingPoint
                ? 'ScreenReader.TripDetails.Details.DepartureFromCity'
                : 'ScreenReader.TripDetails.Details.ArrivalInCity',
              cityName,
            )}
          </Text>
          <Text
            aria-hidden
            data-testid={`${dataTestIdPrefix}-city`}
            variant={textVariants._16_BOLD}
          >
            {cityName}
          </Text>
        </Stack>
        <Text>
          <span data-testid={`${dataTestIdPrefix}-location-name`}>{airportName}</span>{' '}
          <span aria-hidden>(</span>
          <span data-testid={`${dataTestIdPrefix}-location-code`}>{airportCode}</span>
          <span aria-hidden>)</span>
        </Text>
      </Stack>
    </TimelineRow>
  );
};

export default ItineraryLocationTime;
